import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { NavigationType, useLocation, useNavigate, useNavigationType } from 'react-router-dom'

/**
 * Navigation-trail store (QTY-1908..1911).
 *
 * Breadcrumbs on detail pages must reflect the path the user actually
 * navigated (Sale List › {Sale} › {Member}) instead of a fixed per-route
 * hierarchy, and clicking a crumb must behave exactly like pressing the
 * browser Back button the right number of times so the target page comes
 * back with its state (tab, filters) intact.
 *
 * The provider mirrors the browser history stack: one entry per history
 * entry, identified by `location.key`. Pages that participate in trails
 * register a crumb against their history entry (label + optional trail-root
 * info). A detail page can then ask for its ancestor crumbs — the chain of
 * registered entries walking back to the nearest trail root (a list page).
 * If the chain is incomplete (deep link, refresh, or an unregistered page in
 * between) the page falls back to its static default hierarchy.
 *
 * The store is intentionally in-memory only: after a full reload there is no
 * in-app history to step back through, so the static fallback is correct.
 */

export interface TrailCrumbConfig {
  /** Label shown for this page when it appears in a descendant's breadcrumb. */
  label: string
  /** Trail composition stops at (and includes) this entry. Set on list pages. */
  isRoot?: boolean
  /** Non-clickable section crumb rendered before a root (e.g. "Sale Management"). */
  sectionLabel?: string
  /** Sidebar menu section `value` to highlight while inside this trail. */
  menuValue?: string
  /** Sidebar menu item `to` to highlight while inside this trail. */
  menuTo?: string
}

interface TrailEntry {
  key: string
  pathname: string
  search: string
  crumb?: TrailCrumbConfig
}

export interface TrailBreadcrumbItem {
  label: string
  href?: string
  onClick?: () => void
}

export interface TrailAncestry {
  /** Section crumb + one clickable crumb per ancestor page, root first. */
  items: TrailBreadcrumbItem[]
  /** Pathname of the immediate predecessor page in the trail. */
  parentPathname: string
}

interface TrailContextValue {
  version: number
  getEntries: () => TrailEntry[]
  register: (key: string, crumb: TrailCrumbConfig) => void
  goBackTo: (key: string) => void
}

const NavigationTrailContext = createContext<TrailContextValue | null>(null)

const crumbsEqual = (a: TrailCrumbConfig | undefined, b: TrailCrumbConfig) =>
  !!a &&
  a.label === b.label &&
  a.isRoot === b.isRoot &&
  a.sectionLabel === b.sectionLabel &&
  a.menuValue === b.menuValue &&
  a.menuTo === b.menuTo

export const NavigationTrailProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation()
  const navigationType = useNavigationType()
  const navigate = useNavigate()
  const [version, setVersion] = useState(0)

  const stateRef = useRef<{ entries: TrailEntry[]; index: number }>({ entries: [], index: -1 })
  const lastKeyRef = useRef<string | null>(null)

  // Sync the stack during render (idempotent, keyed on location.key) so that
  // page components registering their crumb in an effect always find their
  // entry already present — child effects run before this provider's would.
  if (lastKeyRef.current !== location.key) {
    const state = stateRef.current
    const entry: TrailEntry = {
      key: location.key,
      pathname: location.pathname,
      search: location.search,
    }

    if (navigationType === NavigationType.Pop) {
      const existing = state.entries.findIndex((e) => e.key === location.key)
      if (existing >= 0) {
        state.index = existing
      } else {
        // Initial load (or a history entry from before a full reload):
        // there is no in-app trail to step back through.
        state.entries = [entry]
        state.index = 0
      }
    } else if (navigationType === NavigationType.Replace) {
      const current = state.entries[Math.max(state.index, 0)]
      // Same-page replaces (tab synced into the query string) keep the crumb.
      if (current && current.pathname === location.pathname) {
        entry.crumb = current.crumb
      }
      state.entries = [...state.entries]
      state.entries[Math.max(state.index, 0)] = entry
      state.index = Math.max(state.index, 0)
    } else {
      state.entries = [...state.entries.slice(0, state.index + 1), entry]
      state.index = state.entries.length - 1
    }

    lastKeyRef.current = location.key
  }

  const getEntries = useCallback(() => stateRef.current.entries, [])

  const register = useCallback((key: string, crumb: TrailCrumbConfig) => {
    const entry = stateRef.current.entries.find((e) => e.key === key)
    if (!entry || crumbsEqual(entry.crumb, crumb)) return
    entry.crumb = crumb
    setVersion((v) => v + 1)
  }, [])

  const goBackTo = useCallback(
    (key: string) => {
      const { entries, index } = stateRef.current
      const target = entries.findIndex((e) => e.key === key)
      if (target >= 0 && target < index) {
        void navigate(target - index)
      }
    },
    [navigate]
  )

  const value = useMemo(
    () => ({ version, getEntries, register, goBackTo }),
    [version, getEntries, register, goBackTo]
  )

  return <NavigationTrailContext.Provider value={value}>{children}</NavigationTrailContext.Provider>
}

const useTrailContext = () => useContext(NavigationTrailContext)

/**
 * Register the current page in the navigation trail. Safe to call while data
 * is still loading — re-registers when the label resolves. List pages pass
 * `isRoot` (plus section/sidebar info); detail pages just pass their label.
 */
export const useRegisterTrailCrumb = (config: TrailCrumbConfig) => {
  const ctx = useTrailContext()
  const { key } = useLocation()
  const { label, isRoot, sectionLabel, menuValue, menuTo } = config
  const register = ctx?.register

  useEffect(() => {
    register?.(key, { label, isRoot, sectionLabel, menuValue, menuTo })
  }, [register, key, label, isRoot, sectionLabel, menuValue, menuTo])
}

/**
 * Ancestor crumbs for the current page, walking the in-app history back to
 * the nearest trail root. Returns null when there is no complete registered
 * chain (deep link, refresh, unregistered page in between) — callers then
 * render their static default hierarchy.
 */
export const useTrailAncestry = (): TrailAncestry | null => {
  // `ctx` changes identity whenever any crumb registration changes, which
  // keeps this memo in sync with the store.
  const ctx = useTrailContext()
  const { key } = useLocation()

  return useMemo(() => {
    if (!ctx) return null
    const { getEntries, goBackTo } = ctx
    const entries = getEntries()
    const index = entries.findIndex((e) => e.key === key)
    if (index <= 0) return null

    const chain: TrailEntry[] = []
    for (let i = index - 1; i >= 0; i--) {
      const entry = entries[i]
      if (!entry.crumb) return null
      chain.unshift(entry)
      if (entry.crumb.isRoot) {
        const items: TrailBreadcrumbItem[] = []
        if (entry.crumb.sectionLabel) {
          items.push({ label: entry.crumb.sectionLabel })
        }
        for (const link of chain) {
          items.push({
            label: link.crumb!.label,
            href: `${link.pathname}${link.search}`,
            onClick: () => goBackTo(link.key),
          })
        }
        return { items, parentPathname: entries[index - 1].pathname }
      }
    }
    return null
  }, [ctx, key])
}

/**
 * Sidebar section override for the current page: the trail root's menu
 * section, so e.g. a Member Detail reached from a Sale keeps "Sale
 * Management" highlighted. Null when the current page is not part of a
 * registered trail — the sidebar then falls back to pathname matching.
 */
export const useTrailSection = (): { menuValue: string; menuTo: string } | null => {
  // `ctx` changes identity whenever any crumb registration changes.
  const ctx = useTrailContext()
  const { key } = useLocation()

  return useMemo(() => {
    if (!ctx) return null
    const entries = ctx.getEntries()
    const index = entries.findIndex((e) => e.key === key)
    if (index < 0) return null

    for (let i = index; i >= 0; i--) {
      const crumb = entries[i].crumb
      if (!crumb) return null
      if (crumb.isRoot) {
        return crumb.menuValue && crumb.menuTo
          ? { menuValue: crumb.menuValue, menuTo: crumb.menuTo }
          : null
      }
    }
    return null
  }, [ctx, key])
}
